<script setup>
import { computed, reactive, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { required, between, minValue } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import CarListingsAPI from 'dashboard/api/wgpt/carListings';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PhotoSlotUploader from '../components/PhotoSlotUploader.vue';
import VideoUploader from '../components/VideoUploader.vue';
import ListingStrengthBar from '../components/ListingStrengthBar.vue';
import CatalogField from '../components/CatalogField.vue';
import PublishCreditDialog from '../components/PublishCreditDialog.vue';
import SocialPublishDialog from '../components/SocialPublishDialog.vue';
import { catalogItemLabel } from '../helpers';

const { t, locale } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const currentYear = computed(() => new Date().getFullYear());
const maxYear = computed(() => currentYear.value + 1);
const CURRENCIES = ['usd', 'eur', 'jod', 'sar', 'aed'];
const MILEAGE_UNITS = ['km', 'mi'];
const CONDITIONS = ['new', 'used'];
const BODY_TYPES = [
  'sedan',
  'suv',
  'crossover',
  'hatchback',
  'coupe',
  'convertible',
  'station_wagon',
  'pickup',
  'mpv',
  'van_bus',
];
const TRANSMISSIONS = ['automatic', 'manual'];
const DRIVETRAINS = ['fwd', 'rwd', 'awd', 'four_wd'];
const FUEL_TYPES = ['petrol', 'diesel', 'hybrid', 'electric'];

const listingId = computed(() => route.params.listingId);
const isEditMode = computed(() => !!listingId.value);

const uiFlags = useMapGetter('wgptCarListings/getUIFlags');
const catalog = useMapGetter('wgptCarCatalog/getCatalog');
const getModels = useMapGetter('wgptCarCatalog/getModels');
const getTrims = useMapGetter('wgptCarCatalog/getTrims');
const getCarListing = useMapGetter('wgptCarListings/getCarListing');
const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabled = useMapGetter('accounts/isFeatureEnabledonAccount');

const isSocialEnabled = computed(() =>
  isFeatureEnabled.value(accountId.value, FEATURE_FLAGS.WGPT_SOCIAL_MEDIA_POSTS)
);

const isSaving = computed(
  () => uiFlags.value.isCreating || uiFlags.value.isUpdating
);

const form = reactive({
  title: '',
  reference: '',
  location: '',
  description: '',
  list_price: '',
  final_price: '',
  currency: '',
  wgpt_car_make_id: '',
  wgpt_car_model_id: '',
  wgpt_car_trim_id: '',
  condition: '',
  body_type: '',
  transmission: '',
  drivetrain: '',
  fuel_type: '',
  wgpt_car_color_id: '',
  secondary_wgpt_car_color_id: '',
  interior_wgpt_car_color_id: '',
  year: '',
  mileage: '',
  mileage_unit: '',
  engine_size: '',
  doors: '',
  seating_capacity: '',
  owners: '',
  quantity: 1,
  vin: '',
  plate_number: '',
});

const rules = {
  title: { required },
  wgpt_car_make_id: { required },
  wgpt_car_model_id: { required },
  year: { between: between(1900, maxYear) },
  list_price: { minValue: minValue(0) },
  final_price: {
    minValue: minValue(0),
    belowListPrice: (value, siblings) =>
      value === '' ||
      value == null ||
      !siblings.list_price ||
      Number(value) < Number(siblings.list_price),
  },
  currency: { required },
  mileage: { minValue: minValue(0) },
  mileage_unit: { required },
  quantity: { minValue: minValue(1) },
};

const v$ = useVuelidate(rules, form);

const finalPriceError = () => {
  const field = v$.value.final_price;
  if (!field.$error) return '';
  return field.belowListPrice.$invalid
    ? t('WGPT_CAR_LISTINGS.EDITOR.VALIDATION.FINAL_PRICE_BELOW')
    : t('WGPT_CAR_LISTINGS.EDITOR.VALIDATION.PRICE_POSITIVE');
};

const fieldErrors = computed(() => ({
  title: v$.value.title.$error
    ? t('WGPT_CAR_LISTINGS.EDITOR.VALIDATION.TITLE_REQUIRED')
    : '',
  make: v$.value.wgpt_car_make_id.$error
    ? t('WGPT_CAR_LISTINGS.EDITOR.VALIDATION.MAKE_REQUIRED')
    : '',
  model: v$.value.wgpt_car_model_id.$error
    ? t('WGPT_CAR_LISTINGS.EDITOR.VALIDATION.MODEL_REQUIRED')
    : '',
  year: v$.value.year.$error
    ? t('WGPT_CAR_LISTINGS.EDITOR.VALIDATION.YEAR_RANGE', {
        max: maxYear.value,
      })
    : '',
  list_price: v$.value.list_price.$error
    ? t('WGPT_CAR_LISTINGS.EDITOR.VALIDATION.PRICE_POSITIVE')
    : '',
  final_price: finalPriceError(),
  currency: v$.value.currency.$error
    ? t('WGPT_CAR_LISTINGS.EDITOR.VALIDATION.CURRENCY_REQUIRED')
    : '',
  mileage: v$.value.mileage.$error
    ? t('WGPT_CAR_LISTINGS.EDITOR.VALIDATION.MILEAGE_POSITIVE')
    : '',
  mileage_unit: v$.value.mileage_unit.$error
    ? t('WGPT_CAR_LISTINGS.EDITOR.VALIDATION.MILEAGE_UNIT_REQUIRED')
    : '',
}));

const hydrating = ref(false);

const toOptions = items =>
  (items || []).map(item => ({
    value: item.id,
    label: catalogItemLabel(item, locale.value),
  }));

const makeOptions = computed(() => toOptions(catalog.value.makes));
const modelOptions = computed(() =>
  toOptions(getModels.value(form.wgpt_car_make_id))
);
const trimOptions = computed(() =>
  toOptions(getTrims.value(form.wgpt_car_model_id))
);

const toEnumOptions = (values, i18nKey) =>
  values.map(value => ({
    value,
    label: t(`WGPT_CAR_LISTINGS.${i18nKey}.${value}`),
  }));

const conditionOptions = computed(() =>
  toEnumOptions(CONDITIONS, 'CONDITIONS')
);
const bodyTypeOptions = computed(() => toEnumOptions(BODY_TYPES, 'BODY_TYPES'));
const transmissionOptions = computed(() =>
  toEnumOptions(TRANSMISSIONS, 'TRANSMISSIONS')
);
const drivetrainOptions = computed(() =>
  toEnumOptions(DRIVETRAINS, 'DRIVETRAINS')
);
const fuelTypeOptions = computed(() => toEnumOptions(FUEL_TYPES, 'FUEL_TYPES'));

const currencyOptions = CURRENCIES.map(value => ({
  value,
  label: value.toUpperCase(),
}));
const mileageUnitOptions = MILEAGE_UNITS.map(value => ({
  value,
  label: value,
}));

const listing = computed(() => getCarListing.value(listingId.value));

const optionLabel = (options, value) =>
  options.find(option => option.value === value)?.label || '';

const suggestedTitle = computed(() => {
  if (!form.wgpt_car_make_id || !form.wgpt_car_model_id) return '';
  return [
    optionLabel(makeOptions.value, form.wgpt_car_make_id),
    optionLabel(modelOptions.value, form.wgpt_car_model_id),
    optionLabel(trimOptions.value, form.wgpt_car_trim_id),
    form.year,
  ]
    .filter(Boolean)
    .join(' ');
});

const showSuggestedTitle = computed(
  () => suggestedTitle.value && suggestedTitle.value !== form.title
);

const applySuggestedTitle = () => {
  form.title = suggestedTitle.value;
  v$.value.title.$touch();
};

const strengthScore = computed(() => {
  let score = 0;
  if (form.title?.trim()) score += 10;
  if (form.description?.trim().length >= 30) score += 10;
  if (form.list_price) score += 10;
  if (form.wgpt_car_make_id && form.wgpt_car_model_id) score += 10;
  ['year', 'mileage', 'condition', 'body_type', 'transmission', 'fuel_type']
    .filter(field => form[field] !== '' && form[field] !== null)
    .forEach(() => {
      score += 5;
    });
  if (form.wgpt_car_color_id) score += 5;
  if (form.vin || form.plate_number) score += 5;
  const photoCount = listing.value.photos?.length || 0;
  score += Math.min(photoCount, 4) * 2.5;
  if (photoCount >= 8) score += 5;
  if (listing.value.video) score += 5;
  return Math.round(score);
});

const strengthHint = computed(() => {
  if (!isEditMode.value) return '';
  const photoCount = listing.value.photos?.length || 0;
  if (photoCount < 4) return t('WGPT_CAR_LISTINGS.EDITOR.STRENGTH.HINT_PHOTOS');
  if (!listing.value.video)
    return t('WGPT_CAR_LISTINGS.EDITOR.STRENGTH.HINT_VIDEO');
  if ((form.description?.trim().length || 0) < 30)
    return t('WGPT_CAR_LISTINGS.EDITOR.STRENGTH.HINT_DESCRIPTION');
  if (!form.list_price)
    return t('WGPT_CAR_LISTINGS.EDITOR.STRENGTH.HINT_PRICE');
  return '';
});

const statusActions = computed(() => {
  if (!isEditMode.value) return [];
  switch (listing.value.status) {
    case 'draft':
      return [
        {
          status: 'active',
          label: t('WGPT_CAR_LISTINGS.EDITOR.ACTIONS.PUBLISH'),
          color: 'blue',
        },
      ];
    case 'active':
      return [
        {
          status: 'sold',
          label: t('WGPT_CAR_LISTINGS.EDITOR.ACTIONS.MARK_SOLD'),
          color: 'teal',
        },
        {
          status: 'archived',
          label: t('WGPT_CAR_LISTINGS.EDITOR.ACTIONS.ARCHIVE'),
          color: 'slate',
        },
      ];
    default:
      return [
        {
          status: 'active',
          label: t('WGPT_CAR_LISTINGS.EDITOR.ACTIONS.REACTIVATE'),
          color: 'blue',
        },
      ];
  }
});

watch(
  () => form.wgpt_car_make_id,
  makeId => {
    if (!hydrating.value) {
      form.wgpt_car_model_id = '';
      form.wgpt_car_trim_id = '';
    }
    if (makeId) store.dispatch('wgptCarCatalog/getModels', makeId);
  }
);

watch(
  () => form.wgpt_car_model_id,
  modelId => {
    if (!hydrating.value) form.wgpt_car_trim_id = '';
    if (modelId) store.dispatch('wgptCarCatalog/getTrims', modelId);
    hydrating.value = false;
  }
);

const hydrateForm = data => {
  hydrating.value = true;
  Object.keys(form).forEach(key => {
    if (data[key] !== null && data[key] !== undefined) form[key] = data[key];
  });
};

onMounted(async () => {
  if (isEditMode.value) {
    const data = await store.dispatch('wgptCarListings/show', listingId.value);
    if (data) hydrateForm(data);
    return;
  }

  // Create mode: seed currency/mileage unit from the dealership's configured
  // listing defaults, keeping the built-in fallbacks when a default isn't set.
  try {
    const { data } = await CarListingsAPI.getSettings();
    if (data.defaults?.currency) form.currency = data.defaults.currency;
    if (data.defaults?.mileage_unit)
      form.mileage_unit = data.defaults.mileage_unit;
  } catch (error) {
    // Keep the built-in fallbacks (currency 'sar', mileage unit 'km').
  }
});

const buildPayload = () => {
  const payload = {};
  Object.entries(form).forEach(([key, value]) => {
    payload[key] = value === '' ? null : value;
  });
  // blank reference: auto-generated on create, left unchanged on update
  if (!payload.reference) delete payload.reference;
  return payload;
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) {
    useAlert(t('WGPT_CAR_LISTINGS.EDITOR.VALIDATION.FORM_INVALID'));
    return;
  }

  try {
    if (isEditMode.value) {
      await store.dispatch('wgptCarListings/update', {
        id: listingId.value,
        ...buildPayload(),
      });
      useAlert(t('WGPT_CAR_LISTINGS.EDITOR.API.UPDATE_SUCCESS_MESSAGE'));
    } else {
      const created = await store.dispatch(
        'wgptCarListings/create',
        buildPayload()
      );
      if (created) {
        useAlert(t('WGPT_CAR_LISTINGS.EDITOR.API.CREATE_SUCCESS_MESSAGE'));
        router.replace({
          name: 'wgpt_car_listings_editor',
          params: { listingId: created.id },
        });
      }
    }
  } catch (error) {
    useAlert(error?.message || t('WGPT_CAR_LISTINGS.EDITOR.API.ERROR_MESSAGE'));
  }
};

const creditDialogRef = ref(null);

const handlePublish = async () => {
  try {
    await store.dispatch('wgptCarListings/publish', listingId.value);
    useAlert(t('WGPT_CAR_LISTINGS.EDITOR.ACTIONS.STATUS_UPDATED'));
  } catch (error) {
    if (error?.response?.data?.code === 'LISTING_CREDIT_REQUIRED') {
      creditDialogRef.value?.open();
      return;
    }
    useAlert(
      error?.response?.data?.error ||
        error?.message ||
        t('WGPT_CAR_LISTINGS.EDITOR.API.ERROR_MESSAGE')
    );
  }
};

const handleStatusAction = async action => {
  if (action.status === 'active') {
    await handlePublish();
    return;
  }
  try {
    await store.dispatch('wgptCarListings/unpublish', {
      id: listingId.value,
      status: action.status,
    });
    useAlert(t('WGPT_CAR_LISTINGS.EDITOR.ACTIONS.STATUS_UPDATED'));
  } catch (error) {
    useAlert(error?.message || t('WGPT_CAR_LISTINGS.EDITOR.API.ERROR_MESSAGE'));
  }
};

const socialDialogRef = ref(null);

// Only a live listing can be shared on social media.
const canShare = computed(() => listing.value.status === 'active');

const handleShare = () => socialDialogRef.value?.open(listing.value);

const handleBack = () => {
  router.push({ name: 'wgpt_car_listings_index' });
};
</script>

<template>
  <div class="flex flex-col flex-1 w-full overflow-auto">
    <header
      class="sticky top-0 z-10 flex flex-wrap items-center gap-3 px-6 py-3 border-b bg-n-surface-1 border-n-weak"
    >
      <Button
        icon="i-lucide-arrow-left"
        variant="ghost"
        color="slate"
        size="sm"
        class="rtl:rotate-180"
        @click="handleBack"
      />
      <div class="flex flex-col min-w-0">
        <div class="flex items-center gap-2">
          <h1 class="text-base font-medium truncate text-n-slate-12">
            {{
              isEditMode
                ? t('WGPT_CAR_LISTINGS.EDITOR.EDIT_TITLE')
                : t('WGPT_CAR_LISTINGS.EDITOR.NEW_TITLE')
            }}
          </h1>
          <span
            v-if="isEditMode && listing.reference"
            class="text-xs text-n-slate-10"
          >
            {{ listing.reference }}
          </span>
          <span
            v-if="isEditMode && listing.status"
            class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-11"
          >
            {{ t(`WGPT_CAR_LISTINGS.STATUS.${listing.status}`) }}
          </span>
        </div>
        <span v-if="strengthHint" class="text-xs truncate text-n-slate-10">
          {{ strengthHint }}
        </span>
      </div>
      <div class="flex items-center gap-3 ms-auto">
        <ListingStrengthBar :score="strengthScore" />
        <Button
          v-for="action in statusActions"
          :key="action.status"
          :label="action.label"
          :color="action.color"
          variant="faded"
          size="sm"
          @click="handleStatusAction(action)"
        />
        <span
          v-if="isEditMode && isSocialEnabled"
          v-tooltip.bottom="
            canShare ? null : t('WGPT_CAR_LISTINGS.SOCIAL.UNPUBLISHED_HINT')
          "
        >
          <Button
            :label="t('WGPT_CAR_LISTINGS.SOCIAL.ACTION')"
            icon="i-lucide-share-2"
            variant="faded"
            color="slate"
            size="sm"
            :disabled="!canShare"
            @click="handleShare"
          />
        </span>
        <Button
          :label="
            isEditMode
              ? t('WGPT_CAR_LISTINGS.EDITOR.FORM.SAVE')
              : t('WGPT_CAR_LISTINGS.EDITOR.FORM.CREATE')
          "
          size="sm"
          :is-loading="isSaving"
          @click="handleSubmit"
        />
      </div>
    </header>

    <div
      v-if="uiFlags.isFetchingItem"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>

    <form
      v-else
      class="flex flex-col w-full max-w-4xl gap-8 px-6 py-6 mx-auto"
      @submit.prevent="handleSubmit"
    >
      <section class="flex flex-col gap-4">
        <h2 class="text-base font-medium text-n-slate-12">
          {{ t('WGPT_CAR_LISTINGS.EDITOR.SECTIONS.DETAILS') }}
        </h2>
        <div class="flex flex-col gap-1">
          <Input
            v-model="form.title"
            :label="t('WGPT_CAR_LISTINGS.EDITOR.FORM.TITLE.LABEL')"
            :placeholder="t('WGPT_CAR_LISTINGS.EDITOR.FORM.TITLE.PLACEHOLDER')"
            :message="fieldErrors.title"
            :message-type="fieldErrors.title ? 'error' : 'info'"
            @blur="v$.title.$touch()"
          />
          <button
            v-if="showSuggestedTitle"
            type="button"
            class="self-start px-2 py-1 text-xs rounded-md text-n-blue-11 bg-n-blue-3 hover:bg-n-blue-4 transition-colors"
            @click="applySuggestedTitle"
          >
            {{
              t('WGPT_CAR_LISTINGS.EDITOR.SUGGESTED_TITLE', {
                title: suggestedTitle,
              })
            }}
          </button>
        </div>
        <div class="grid grid-cols-2 gap-4">
          <Input
            v-model="form.reference"
            :label="t('WGPT_CAR_LISTINGS.EDITOR.FORM.REFERENCE.LABEL')"
            :placeholder="
              t('WGPT_CAR_LISTINGS.EDITOR.FORM.REFERENCE.PLACEHOLDER')
            "
          />
          <Input
            v-model="form.location"
            :label="t('WGPT_CAR_LISTINGS.EDITOR.FORM.LOCATION.LABEL')"
            :placeholder="
              t('WGPT_CAR_LISTINGS.EDITOR.FORM.LOCATION.PLACEHOLDER')
            "
          />
        </div>
        <TextArea
          v-model="form.description"
          :label="t('WGPT_CAR_LISTINGS.EDITOR.FORM.DESCRIPTION.LABEL')"
          :placeholder="
            t('WGPT_CAR_LISTINGS.EDITOR.FORM.DESCRIPTION.PLACEHOLDER')
          "
          :max-length="2000"
          show-character-count
          auto-height
        />
        <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Input
            v-model="form.list_price"
            type="number"
            :label="t('WGPT_CAR_LISTINGS.EDITOR.FORM.PRICE.LABEL')"
            :placeholder="t('WGPT_CAR_LISTINGS.EDITOR.FORM.PRICE.PLACEHOLDER')"
            :message="fieldErrors.list_price"
            :message-type="fieldErrors.list_price ? 'error' : 'info'"
            @blur="v$.list_price.$touch()"
          />
          <Input
            v-model="form.final_price"
            type="number"
            :label="t('WGPT_CAR_LISTINGS.EDITOR.FORM.FINAL_PRICE.LABEL')"
            :placeholder="
              t('WGPT_CAR_LISTINGS.EDITOR.FORM.FINAL_PRICE.PLACEHOLDER')
            "
            :message="fieldErrors.final_price"
            :message-type="fieldErrors.final_price ? 'error' : 'info'"
            @blur="v$.final_price.$touch()"
          />
          <div class="flex flex-col gap-1">
            <span class="text-sm text-n-slate-11">
              {{ t('WGPT_CAR_LISTINGS.EDITOR.FORM.CURRENCY.LABEL') }}
            </span>
            <ComboBox
              v-model="form.currency"
              :options="currencyOptions"
              :has-error="!!fieldErrors.currency"
              :message="fieldErrors.currency"
            />
          </div>
        </div>
      </section>

      <section class="flex flex-col gap-4">
        <h2 class="text-base font-medium text-n-slate-12">
          {{ t('WGPT_CAR_LISTINGS.EDITOR.SECTIONS.CAR') }}
        </h2>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <CatalogField
            v-model="form.wgpt_car_make_id"
            type="make"
            :label="t('WGPT_CAR_LISTINGS.EDITOR.FORM.MAKE.LABEL')"
            :items="catalog.makes"
            :has-error="!!fieldErrors.make"
            :message="fieldErrors.make"
            :placeholder="t('WGPT_CAR_LISTINGS.EDITOR.FORM.MAKE.PLACEHOLDER')"
          />
          <CatalogField
            v-model="form.wgpt_car_model_id"
            type="model"
            :label="t('WGPT_CAR_LISTINGS.EDITOR.FORM.MODEL.LABEL')"
            :items="getModels(form.wgpt_car_make_id)"
            :parent-id="form.wgpt_car_make_id"
            :disabled="!form.wgpt_car_make_id"
            :has-error="!!fieldErrors.model"
            :message="fieldErrors.model"
            :placeholder="t('WGPT_CAR_LISTINGS.EDITOR.FORM.MODEL.PLACEHOLDER')"
          />
          <CatalogField
            v-model="form.wgpt_car_trim_id"
            type="trim"
            :label="t('WGPT_CAR_LISTINGS.EDITOR.FORM.TRIM.LABEL')"
            :items="getTrims(form.wgpt_car_model_id)"
            :parent-id="form.wgpt_car_model_id"
            :disabled="!form.wgpt_car_model_id"
            :placeholder="t('WGPT_CAR_LISTINGS.EDITOR.FORM.TRIM.PLACEHOLDER')"
          />
          <Input
            v-model="form.year"
            type="number"
            :label="t('WGPT_CAR_LISTINGS.EDITOR.FORM.YEAR.LABEL')"
            :placeholder="t('WGPT_CAR_LISTINGS.EDITOR.FORM.YEAR.PLACEHOLDER')"
            :message="fieldErrors.year"
            :message-type="fieldErrors.year ? 'error' : 'info'"
            @blur="v$.year.$touch()"
          />
          <div class="flex flex-col gap-1">
            <span class="text-sm text-n-slate-11">
              {{ t('WGPT_CAR_LISTINGS.EDITOR.FORM.CONDITION.LABEL') }}
            </span>
            <ComboBox
              v-model="form.condition"
              :options="conditionOptions"
              :placeholder="
                t('WGPT_CAR_LISTINGS.EDITOR.FORM.CONDITION.PLACEHOLDER')
              "
            />
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-sm text-n-slate-11">
              {{ t('WGPT_CAR_LISTINGS.EDITOR.FORM.BODY_TYPE.LABEL') }}
            </span>
            <ComboBox
              v-model="form.body_type"
              :options="bodyTypeOptions"
              :placeholder="
                t('WGPT_CAR_LISTINGS.EDITOR.FORM.BODY_TYPE.PLACEHOLDER')
              "
            />
          </div>
        </div>
      </section>

      <section class="flex flex-col gap-4">
        <h2 class="text-base font-medium text-n-slate-12">
          {{ t('WGPT_CAR_LISTINGS.EDITOR.SECTIONS.SPECS') }}
        </h2>
        <div class="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <div class="flex flex-col gap-1">
            <span class="text-sm text-n-slate-11">
              {{ t('WGPT_CAR_LISTINGS.EDITOR.FORM.TRANSMISSION.LABEL') }}
            </span>
            <ComboBox
              v-model="form.transmission"
              :options="transmissionOptions"
              :placeholder="
                t('WGPT_CAR_LISTINGS.EDITOR.FORM.TRANSMISSION.PLACEHOLDER')
              "
            />
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-sm text-n-slate-11">
              {{ t('WGPT_CAR_LISTINGS.EDITOR.FORM.DRIVETRAIN.LABEL') }}
            </span>
            <ComboBox
              v-model="form.drivetrain"
              :options="drivetrainOptions"
              :placeholder="
                t('WGPT_CAR_LISTINGS.EDITOR.FORM.DRIVETRAIN.PLACEHOLDER')
              "
            />
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-sm text-n-slate-11">
              {{ t('WGPT_CAR_LISTINGS.EDITOR.FORM.FUEL_TYPE.LABEL') }}
            </span>
            <ComboBox
              v-model="form.fuel_type"
              :options="fuelTypeOptions"
              :placeholder="
                t('WGPT_CAR_LISTINGS.EDITOR.FORM.FUEL_TYPE.PLACEHOLDER')
              "
            />
          </div>
          <CatalogField
            v-model="form.wgpt_car_color_id"
            type="color"
            :label="t('WGPT_CAR_LISTINGS.EDITOR.FORM.COLOR.LABEL')"
            :items="catalog.colors"
            :placeholder="t('WGPT_CAR_LISTINGS.EDITOR.FORM.COLOR.PLACEHOLDER')"
          />
          <CatalogField
            v-model="form.secondary_wgpt_car_color_id"
            type="color"
            :label="t('WGPT_CAR_LISTINGS.EDITOR.FORM.SECONDARY_COLOR.LABEL')"
            :items="catalog.colors"
            :placeholder="
              t('WGPT_CAR_LISTINGS.EDITOR.FORM.SECONDARY_COLOR.PLACEHOLDER')
            "
          />
          <CatalogField
            v-model="form.interior_wgpt_car_color_id"
            type="color"
            :label="t('WGPT_CAR_LISTINGS.EDITOR.FORM.INTERIOR_COLOR.LABEL')"
            :items="catalog.colors"
            :placeholder="
              t('WGPT_CAR_LISTINGS.EDITOR.FORM.INTERIOR_COLOR.PLACEHOLDER')
            "
          />
          <Input
            v-model="form.mileage"
            type="number"
            :label="t('WGPT_CAR_LISTINGS.EDITOR.FORM.MILEAGE.LABEL')"
            :placeholder="
              t('WGPT_CAR_LISTINGS.EDITOR.FORM.MILEAGE.PLACEHOLDER')
            "
            :message="fieldErrors.mileage"
            :message-type="fieldErrors.mileage ? 'error' : 'info'"
            @blur="v$.mileage.$touch()"
          />
          <div class="flex flex-col gap-1">
            <span class="text-sm text-n-slate-11">
              {{ t('WGPT_CAR_LISTINGS.EDITOR.FORM.MILEAGE_UNIT.LABEL') }}
            </span>
            <ComboBox
              v-model="form.mileage_unit"
              :options="mileageUnitOptions"
              :has-error="!!fieldErrors.mileage_unit"
              :message="fieldErrors.mileage_unit"
            />
          </div>
          <Input
            v-model="form.engine_size"
            type="number"
            :label="t('WGPT_CAR_LISTINGS.EDITOR.FORM.ENGINE_SIZE.LABEL')"
            :placeholder="
              t('WGPT_CAR_LISTINGS.EDITOR.FORM.ENGINE_SIZE.PLACEHOLDER')
            "
          />
          <Input
            v-model="form.doors"
            type="number"
            :label="t('WGPT_CAR_LISTINGS.EDITOR.FORM.DOORS.LABEL')"
            :placeholder="t('WGPT_CAR_LISTINGS.EDITOR.FORM.DOORS.PLACEHOLDER')"
          />
          <Input
            v-model="form.seating_capacity"
            type="number"
            :label="t('WGPT_CAR_LISTINGS.EDITOR.FORM.SEATING_CAPACITY.LABEL')"
            :placeholder="
              t('WGPT_CAR_LISTINGS.EDITOR.FORM.SEATING_CAPACITY.PLACEHOLDER')
            "
          />
          <Input
            v-model="form.owners"
            type="number"
            :label="t('WGPT_CAR_LISTINGS.EDITOR.FORM.OWNERS.LABEL')"
            :placeholder="t('WGPT_CAR_LISTINGS.EDITOR.FORM.OWNERS.PLACEHOLDER')"
          />
          <Input
            v-model="form.quantity"
            type="number"
            :label="t('WGPT_CAR_LISTINGS.EDITOR.FORM.QUANTITY.LABEL')"
            :placeholder="
              t('WGPT_CAR_LISTINGS.EDITOR.FORM.QUANTITY.PLACEHOLDER')
            "
          />
          <Input
            v-model="form.vin"
            :label="t('WGPT_CAR_LISTINGS.EDITOR.FORM.VIN.LABEL')"
            :placeholder="t('WGPT_CAR_LISTINGS.EDITOR.FORM.VIN.PLACEHOLDER')"
          />
          <Input
            v-model="form.plate_number"
            :label="t('WGPT_CAR_LISTINGS.EDITOR.FORM.PLATE_NUMBER.LABEL')"
            :placeholder="
              t('WGPT_CAR_LISTINGS.EDITOR.FORM.PLATE_NUMBER.PLACEHOLDER')
            "
          />
        </div>
      </section>

      <section class="flex flex-col gap-4">
        <h2 class="text-base font-medium text-n-slate-12">
          {{ t('WGPT_CAR_LISTINGS.EDITOR.SECTIONS.PHOTOS') }}
        </h2>
        <PhotoSlotUploader v-if="isEditMode" :listing="listing" />
        <p v-else class="text-sm text-n-slate-11">
          {{ t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.SAVE_FIRST') }}
        </p>
      </section>

      <section class="flex flex-col gap-4 pb-10">
        <h2 class="text-base font-medium text-n-slate-12">
          {{ t('WGPT_CAR_LISTINGS.EDITOR.SECTIONS.VIDEO') }}
        </h2>
        <VideoUploader v-if="isEditMode" :listing="listing" />
        <p v-else class="text-sm text-n-slate-11">
          {{ t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.SAVE_FIRST') }}
        </p>
      </section>
    </form>
    <PublishCreditDialog ref="creditDialogRef" />
    <SocialPublishDialog ref="socialDialogRef" />
  </div>
</template>
